import React, { Component, FC, ReactNode, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { Button, Col, Container, Row } from 'reactstrap';
import { useTranslation } from '../translations';
import { captureError, getSentry } from '../utils/errorHandling';
import { MaterialIcon, PageHeader, RichMarkdownIt } from '.';

import './ErrorBoundary.scss';

interface Props {
  readonly children?: ReactNode;
  readonly redirectText?: string;
  readonly redirectTo?: string;
}

interface State {
  error?: Error;
  sentryErrorId?: string;
}

const ErrorBody: FC<{ readonly sentryErrorId?: string }> = ({ sentryErrorId }) => {
  const { ts } = useTranslation();

  return (
    <>
      <PageHeader>{ts('error_boundary.oops_well_this_is_awkward')}</PageHeader>
      <p>{ts('error_boundary.it_looks_like_there_was')}</p>
      <p>{ts('error_boundary.weve_logged_the_error_and')}</p>
      <hr />
      <div>
        {ts('error_boundary.in_the_meantime_heres_what')}
        <ul className="list-unstyled">
          <li>
            <MaterialIcon name="refresh" />
            <RichMarkdownIt source={ts('error_boundary.refresh_the_page_sometimes_that')} />
          </li>
          <li>
            <MaterialIcon name="access_time" />
            <RichMarkdownIt source={ts('error_boundary.try_again_in_a_little')} />
          </li>
          {sentryErrorId && (
            <li>
              <MaterialIcon name="bug_report" />
              {ts('error_boundary.provide_additional_information_to_help')}
            </li>
          )}
        </ul>
      </div>
    </>
  );
};

interface ErrorBoundaryInnerProps {
  readonly redirectText?: string;
  readonly redirectTo?: string;
  readonly error: Error;
  readonly sentryErrorId?: string;
}

const ErrorBoundaryInner: FC<ErrorBoundaryInnerProps> = ({
  error,
  sentryErrorId,
  redirectText,
  redirectTo,
}) => {
  const { ts } = useTranslation();
  const history = useHistory();

  const handleClickReport = useCallback(() => {
    if (!sentryErrorId) {
      return;
    }

    const sntry = getSentry();
    if (!sntry) {
      return;
    }

    sntry.showReportDialog({
      errorGeneric:
        'An error occurred while submitting your report to Sentry. Try again in a few moments.',
      eventId: sentryErrorId,
      subtitle:
        'We have already logged the error, but additional information will help us resolve the issue quicker.',
      subtitle2:
        'Please provide as much information on what happened as you feel would be helpful.',
      successMessage:
        'Thanks for your help! We have attached your additional info to the error ticket.',
      title: `Error Reporting - ${sentryErrorId}`,
    });
  }, [sentryErrorId]);

  return (
    <Container className="mt-3 react-error">
      <Row className="justify-content-center">
        <Col className="mb-10" lg={5} xs={12}>
          <ErrorBody sentryErrorId={sentryErrorId} />
          <hr />
        </Col>
        <Col xs={12} />
        {sentryErrorId && (
          <Col className="mb-4" lg={2} xs={12}>
            <Button block color="primary" onClick={handleClickReport}>
              {ts('error_boundary.provide_additional_info')}
            </Button>
          </Col>
        )}
        {history.location.pathname !== '/' && (
          <Col className="mb-4" lg={2} xs={12}>
            <Button
              block
              color="secondary"
              onClick={() => {
                history.push(redirectTo ?? '/');
              }}
              outline
            >
              {redirectText ?? ts('error_boundary.back_to_the_homepage')}
            </Button>
          </Col>
        )}
        <Col xs={12} />
        <Col lg={5} xs={12}>
          <p className="text-secondary">
            <small>
              {ts('error_boundary.uncaught_exception')} &mdash; {error.name}: {error.message}
            </small>
          </p>
        </Col>
      </Row>
    </Container>
  );
};

export class ErrorBoundary extends Component<Props, State> {
  public static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  public constructor(props: Props) {
    super(props);
    this.state = {};
  }

  public override componentDidCatch(err: Error): void {
    this.setState({
      sentryErrorId: captureError(err),
    });
  }

  public override render(): React.JSX.Element | ReactNode {
    const { error, sentryErrorId } = this.state;
    const { children, redirectTo, redirectText } = this.props;

    if (!error) {
      return children;
    }

    return (
      <ErrorBoundaryInner
        error={error}
        redirectText={redirectText}
        redirectTo={redirectTo}
        sentryErrorId={sentryErrorId}
      />
    );
  }
}
